import { Camera, MessageCircle, SquarePen } from 'lucide-preact'
import { useEffect, useState } from 'preact/hooks'
import { text } from '#contract'
import { useClient } from '../../../app/client-context.ts'
import { useView } from '../../../app/use-view.ts'
import { useT } from '../../../i18n/index.ts'
import { WassupIcon } from '../../icons/app-icons.tsx'
import { AppEmptyState } from '../app-empty-state.tsx'
import appStyles from '../apps.module.css'
import type { OsAppProps } from '../types.ts'
import { ChatList } from './chat-list.tsx'
import { Thread } from './thread.tsx'
import styles from './wassup.module.css'

/**
 * Wassup (a WhatsApp parody): the chats, the latest first, and a chat's messages. The phone
 * shows one at a time (Back returns to the list); the desktop shows the list beside the open
 * chat. A chat in view on the device that's up is read.
 */
export function WassupApp({ os }: OsAppProps) {
  const t = useT()
  const client = useClient()
  const chats = useView((view) => view.messages.chats)
  const device = os.form === 'phone' ? 'phone' : 'computer'
  const shown = useView((view) => view.os.open === device) && os.front.value === 'wassup'
  const [selected, setSelected] = useState<string | null>(null)
  const chat = chats.find((c) => c.id === selected) ?? null
  const phone = os.form === 'phone'

  useEffect(() => {
    os.setBack('wassup', () => {
      if (selected === null) return false
      setSelected(null)
      return true
    })
    return () => {
      os.setBack('wassup', null)
    }
  }, [os, selected])

  const unread = chat?.unread ?? 0
  useEffect(() => {
    if (shown && chat && unread > 0) client?.messages.read(chat.id)
  }, [shown, chat, unread, client])

  if (phone && chat) {
    return (
      <Thread
        chat={chat}
        onBack={() => {
          setSelected(null)
        }}
      />
    )
  }
  return (
    <div class={[appStyles.app, styles.wassup].join(' ')}>
      <div class={styles.side}>
        <header class={[appStyles.header, appStyles.wassup].join(' ')}>
          <h1 class={appStyles.title}>{t(text('os.app.wassup.chats'))}</h1>
          <span class={appStyles.glyph} aria-hidden="true">
            <Camera />
          </span>
          <span class={appStyles.glyph} aria-hidden="true">
            <SquarePen />
          </span>
        </header>
        {chats.length > 0 ? (
          <ChatList chats={chats} selected={selected} onOpen={setSelected} />
        ) : (
          <AppEmptyState id="wassup" icon={WassupIcon} />
        )}
      </div>
      {!phone &&
        (chat ? (
          <Thread chat={chat} onBack={null} />
        ) : (
          <div class={styles.placeholder}>
            <MessageCircle aria-hidden="true" />
            <p>{t(text('os.app.wassup.pick'))}</p>
          </div>
        ))}
    </div>
  )
}
